import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { TimeRange } from '@mi-front/core';
import { useCallback, useMemo } from 'react';
import {
  StreamSelection,
  useClearSelectionAction,
  useSelectionStore,
  useSetSelectionAction,
  useSetSelectionEndAction,
  useSetSelectionStartAction,
} from './selection.store';

export interface UseStreamSelectionReturn {
  // State
  selection: StreamSelection;
  selectionExists: boolean;
  selectionRange: TimeRange<number>;
  // Actions
  setSelection: (tsA: number | null, tsB: number | null) => void;
  setSelectionStart: (ts: number | null) => void;
  setSelectionEnd: (ts: number | null) => void;
  clearSelection: () => void;
  incrementSelection: (ms: number) => void;
  decrementSelection: (ms: number) => void;
  canStartSelection: (ts: number) => boolean;
  canEndSelection: (ts: number) => boolean;
}

// Optimized hook for when you only need to check if selection exists
// This subscribes to a computed boolean, so it only re-renders when the boolean changes
export function useSelectionExists(): boolean {
  const station = useActiveStation();

  return useSelectionStore((s) => {
    const sel = s.selectionByStationId[station.id];
    return !!sel?.tsA && !!sel?.tsB;
  });
}

// Optimized hook for when you only need to check if partial selection exists
// A partial selection exists when either tsA or tsB is set, but not both
export function usePartialSelectionExists(): boolean {
  const station = useActiveStation();

  return useSelectionStore((s) => {
    const sel = s.selectionByStationId[station.id];
    return !!(sel?.tsA || sel?.tsB);
  });
}

// Main hook that returns both state and actions scoped to active station
export function useStreamSelection(): UseStreamSelectionReturn {
  const station = useActiveStation();

  // Subscribe to primitives directly for optimal re-render behavior
  // Access store state directly to avoid any intermediate object creation
  const tsA = useSelectionStore((s) => s.selectionByStationId[station.id]?.tsA ?? null);
  const tsB = useSelectionStore((s) => s.selectionByStationId[station.id]?.tsB ?? null);

  // Derive selection object and computed values locally
  const selection: StreamSelection = useMemo(() => ({ tsA, tsB }), [tsA, tsB]);

  const selectionExists = useMemo(() => !!(tsA && tsB), [tsA, tsB]);

  const selectionRange: TimeRange<number> = useMemo(
    () => ({
      from: Math.min(tsA ?? Infinity, tsB ?? Infinity),
      to: Math.max(tsA ?? 0, tsB ?? 0),
    }),
    [tsA, tsB],
  );

  // Get action functions from store hooks
  const setSelectionAction = useSetSelectionAction();
  const setSelectionStartAction = useSetSelectionStartAction();
  const setSelectionEndAction = useSetSelectionEndAction();
  const clearSelectionAction = useClearSelectionAction();

  // Memoize actions to ensure stable references
  const setSelection = useCallback(
    (tsA: number | null, tsB: number | null) => {
      setSelectionAction(station.id, tsA, tsB);
    },
    [station.id, setSelectionAction],
  );

  const setSelectionStart = useCallback(
    (ts: number | null) => {
      setSelectionStartAction(station.id, ts);
    },
    [station.id, setSelectionStartAction],
  );

  const setSelectionEnd = useCallback(
    (ts: number | null) => {
      setSelectionEndAction(station.id, ts);
    },
    [station.id, setSelectionEndAction],
  );

  const clearSelection = useCallback(() => {
    clearSelectionAction(station.id);
  }, [station.id, clearSelectionAction]);

  const incrementSelection = useCallback(
    (ms: number) => {
      if (!selectionExists) {
        return;
      }

      setSelection(selectionRange.from - ms, selectionRange.to + ms);
    },
    [station.id, selectionExists, selectionRange, setSelection],
  );

  const decrementSelection = useCallback(
    (ms: number) => {
      if (!selectionExists) {
        return;
      }

      const { from, to } = selectionRange;
      if (from + ms < to - ms) {
        setSelection(from + ms, to - ms);
      }
    },
    [selectionExists, selectionRange, setSelection],
  );

  const canStartSelection = useCallback(
    (ts: number) => {
      // Compute canStartSelection logic based on current selection state
      // Allow to start selection when:
      // - there is no tsA and tsB
      // - the boundary tsA is only set
      // - the boundary tsB is only set, and the cursor lies before it
      // - cursor lies before the maximum of the current boundaries
      return Boolean(
        (!tsA && !tsB) ||
        (tsA && !tsB) ||
        (!tsA && tsB && tsB > ts) ||
        (tsA && tsB && ts < Math.max(tsA, tsB)),
      );
    },
    [tsA, tsB],
  );

  const canEndSelection = useCallback(
    (ts: number) => {
      // Compute canEndSelection logic based on current selection state
      // Allow to end selection when:
      // - there is no tsA and tsB
      // - the boundary tsB is only set
      // - the boundary tsA is only set, and the cursor lies after it
      // - cursor lies after the minimum of the current boundaries
      return Boolean(
        (!tsA && !tsB) ||
        (tsB && !tsA) ||
        (!tsB && tsA && ts > tsA) ||
        (tsA && tsB && ts > Math.min(tsA, tsB)),
      );
    },
    [tsA, tsB],
  );

  return {
    // State
    selection,
    selectionExists,
    selectionRange,
    // Actions
    setSelection,
    setSelectionStart,
    setSelectionEnd,
    clearSelection,
    incrementSelection,
    decrementSelection,
    canStartSelection,
    canEndSelection,
  };
}
